export type AlertType = 'price_anomaly' | 'size_mismatch';
export type AlertFilter = 'all' | 'price_anomaly' | 'size_mismatch' | 'no_alerts';
export type MatchType = 'machine' | 'human';
export type SortDirection = 'asc' | 'desc';
export type SortField = 'sku' | 'brand' | 'name' | 'modifiedAt';

export interface AlertInfo {
  type: AlertType;
  details?: string;
}

export interface NormalizationSettings {
  competitorSize: string;
  competitorPack: string;
  ourSize: string;
  ourPack: string;
  computedRatio: number;
  overrideValue?: number;
  normalizedPrice: number;
}

export interface SkinSeoulProduct {
  id?: string;
  sku: string;
  name: string;
  brand: string;
  category: string[];
  size: string;
  pack: string;
  productImageUrls?: string[];
  productLink?: string;
  type?: string;
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  modifiedAt?: string;
  cogs?: number;
  variations?: SkinSeoulProduct[];
}

export interface PriceInfo {
  currency: string;
  originalPrice?: number;
  salesPrice?: number;
}

export interface CompetitorProduct {
  sku: string;
  name: string;
  brand: string;
  productLink: string;
  category: string;
  size: string;
  pack: string;
  modifiedAt: string | null;
  productImageUrls?: string;
  normalizedName: string;
  possibility: number;
  type?: string;
  price?: PriceInfo[];
  originalPrice?: number;
  salePrice?: number;
  currency?: string;
  matchType?: MatchType;
  normalizationRatio?: number;
  normalizedPrice?: number;
  normalization?: NormalizationSettings;
  alerts?: AlertInfo[];
}

export interface Competitor {
  competitor: string;
  competitorProducts: CompetitorProduct[];
}

export interface ProductData {
  skinSeoulProduct: SkinSeoulProduct;
  machineMatchedProducts: Competitor[];
  humanMatchedProducts?: Competitor[];
  alerts?: AlertInfo[];
}

export interface MatchedProductWithAlerts {
  product: ProductData;
  alerts: string[];
}

export interface ListMatchedProductsResponse {
  items: MatchedProductWithAlerts[];
  limit: number;
  lastEvaluatedKey?: string | null;
}

export interface ProductFiltersState {
  search: string;
  alertType: AlertType | 'all';
  noAlerts: boolean;
}

export interface SortState {
  field: SortField;
  direction: SortDirection;
}

export interface PaginationState {
  limit: number;
  lastEvaluatedKey?: string | null;
}

export interface SelectedProductMap {
  [skinSeoulSku: string]: {
    [competitor: string]: string; // Maps competitor name to selected competitor product SKU
  };
}

// Parse JSON strings to arrays where needed
export const parseJsonArray = (jsonString: string | undefined): string[] => {
  if (!jsonString) {
    return [];
  }
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return [];
  }
};

export const parseSize = (size: string | undefined): number => {
  if (!size) return 0;
  const match = size.match(/(\d+(?:\.\d+)?)/);
  return match && match[1] ? parseFloat(match[1]) : 0;
};

export const parsePack = (pack: string | undefined): number => {
  if (!pack) return 1;
  const match = pack.match(/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 1;
};

export const computeNormalizationRatio = (
  competitorSize: string,
  competitorPack: string,
  ourSize: string,
  ourPack: string,
): number => {
  const compSize = parseSize(competitorSize);
  const compPack = parsePack(competitorPack);
  const ssSize = parseSize(ourSize);
  const ssPack = parsePack(ourPack);

  if (ssSize === 0 || ssPack === 0) return 1;
  const compTotal = compSize * compPack;
  const ssTotal = ssSize * ssPack;
  return ssTotal > 0 ? compTotal / ssTotal : 1;
};

export const detectAlerts = (
  product: SkinSeoulProduct,
  competitorProduct: CompetitorProduct,
): AlertInfo[] => {
  const alerts: AlertInfo[] = [];
  const cogs = product.cogs || 0;
  const competitorPrice = competitorProduct.originalPrice || competitorProduct.salePrice || 0;

  if (competitorPrice > 0 && cogs > 0) {
    const upperBound = 1.5 * cogs * 0.00092 / 0.5;
    const lowerBound = 0.5 * cogs * 0.00092 / 0.5;
    if (competitorPrice > upperBound || competitorPrice < lowerBound) {
      alerts.push({
        type: 'price_anomaly',
        details: `Price ${competitorPrice} outside bounds [${lowerBound.toFixed(2)}, ${upperBound.toFixed(2)}]`,
      });
    }
  }

  const sizeMismatch =
    product.size !== competitorProduct.size || product.pack !== competitorProduct.pack;
  const hasNormalization = !!competitorProduct.normalization?.overrideValue;
  if (sizeMismatch && !hasNormalization) {
    alerts.push({
      type: 'size_mismatch',
      details: `Size/pack differs: ${product.size}/${product.pack} vs ${competitorProduct.size}/${competitorProduct.pack}`,
    });
  }

  return alerts;
};
